import {
	GetRetrospectiveApiResponse,
	RetrospectiveApiBigNumbers,
	RetrospectiveData,
} from "./types";
import { safePercentage } from "./utils";

export function aggregateBigNumbers(
	bigNumbersArray: RetrospectiveApiBigNumbers[],
): RetrospectiveApiBigNumbers {
	if (bigNumbersArray.length === 0) {
		return {} as RetrospectiveApiBigNumbers;
	}

	if (bigNumbersArray.length === 1) {
		return bigNumbersArray[0];
	}

	// Verificar se há tipos mistos (ficha e cashless)
	const tiposOperacao = new Set(bigNumbersArray.map(bn => bn.tipo_operacao));
	const hasFicha = tiposOperacao.has("ficha");
	const hasCashless = tiposOperacao.has("cashless");
	const isMixed = hasFicha && hasCashless;

	// Se for misto, usar "mixed" como tipo_operacao
	const tipoOperacao = isMixed ? "mixed" : bigNumbersArray[0].tipo_operacao;

	const totals = bigNumbersArray.reduce(
		(acc, bn) => {
			acc.dias_operacao += bn.dias_operacao || 0;
			acc.tempo_operacao_total_seg += bn.tempo_operacao_total_seg || 0;
			acc.qtd_pessoas += bn.qtd_pessoas || 0;
			acc.qtd_produtos += bn.qtd_produtos || 0;
			acc.qtd_transacoes += bn.qtd_transacoes || 0;
			acc.checkins += bn.checkins || 0;
			acc.faturamento += bn.faturamento || 0;
			acc.qtd_pessoas_24 += bn.qtd_pessoas_24 || 0;
			acc.qtd_produtos_24 += bn.qtd_produtos_24 || 0;
			acc.qtd_transacoes_24 += bn.qtd_transacoes_24 || 0;
			acc.faturamento_24 += bn.faturamento_24 || 0;
			acc.checkins_24 += bn.checkins_24 || 0;
			acc.qtd_litros_cervejas += bn.qtd_litros_cervejas || 0;
			acc.faturamento_bebidas += bn.faturamento_bebidas || 0;
			acc.faturamento_comidas += bn.faturamento_comidas || 0;
			return acc;
		},
		{
			dias_operacao: 0,
			tempo_operacao_total_seg: 0,
			qtd_pessoas: 0,
			qtd_produtos: 0,
			qtd_transacoes: 0,
			checkins: 0,
			faturamento: 0,
			qtd_pessoas_24: 0,
			qtd_produtos_24: 0,
			qtd_transacoes_24: 0,
			faturamento_24: 0,
			checkins_24: 0,
			qtd_litros_cervejas: 0,
			faturamento_bebidas: 0,
			faturamento_comidas: 0,
		},
	);

	const aggregated: RetrospectiveApiBigNumbers = {
		org_id: "aggregated",
		org_name: "aggregated",
		place_id: "aggregated",
		place_name: "aggregated",
		tipo_operacao: tipoOperacao,
		...totals,
		tempo_operacao_medio_dia_seg:
			totals.dias_operacao > 0
				? totals.tempo_operacao_total_seg / totals.dias_operacao
				: 0,
		tkt_medio_pessoas:
			totals.qtd_pessoas > 0 ? totals.faturamento / totals.qtd_pessoas : 0,
		tkt_medio_pessoas_24:
			totals.qtd_pessoas_24 > 0 ? totals.faturamento_24 / totals.qtd_pessoas_24 : 0,
		tkt_medio_checkins: totals.checkins > 0 ? totals.faturamento / totals.checkins : 0,
		tkt_medio_checkins_24:
			totals.checkins_24 > 0 ? totals.faturamento_24 / totals.checkins_24 : 0,
		tkt_medio_produtos:
			totals.qtd_produtos > 0 ? totals.faturamento / totals.qtd_produtos : 0,
		tkt_medio_produtos_24:
			totals.qtd_produtos_24 > 0 ? totals.faturamento_24 / totals.qtd_produtos_24 : 0,
		horario_pico: bigNumbersArray[0].horario_pico,
		qtd_faturamento_horario_pico: bigNumbersArray[0].qtd_faturamento_horario_pico,
		dia_semana_pico: bigNumbersArray[0].dia_semana_pico,
		qtd_faturamento_dia_semana: bigNumbersArray[0].qtd_faturamento_dia_semana,
		melhor_produto: bigNumbersArray[0].melhor_produto,
		qtd_faturamento_melhor_produto: bigNumbersArray[0].qtd_faturamento_melhor_produto,
		intervalo_horario: bigNumbersArray[0].intervalo_horario,
		// qtd_litros_cervejas é somado porque Curiosidades pode mostrar aggregated
		qtd_litros_cervejas: totals.qtd_litros_cervejas,
		faturamento_bebidas: totals.faturamento_bebidas,
		faturamento_comidas: totals.faturamento_comidas,
	};

	return aggregated;
}

export function aggregateBigNumbersByType(
	bigNumbersArray: RetrospectiveApiBigNumbers[],
	type: "ficha" | "cashless",
): RetrospectiveApiBigNumbers {
	const filtered = bigNumbersArray.filter(bn => bn.tipo_operacao === type);
	if (filtered.length === 0) {
		return {} as RetrospectiveApiBigNumbers;
	}

	const totals = filtered.reduce(
		(acc, bn) => {
			acc.dias_operacao += bn.dias_operacao || 0;
			acc.tempo_operacao_total_seg += bn.tempo_operacao_total_seg || 0;
			acc.qtd_pessoas += bn.qtd_pessoas || 0;
			acc.qtd_produtos += bn.qtd_produtos || 0;
			acc.qtd_transacoes += bn.qtd_transacoes || 0;
			acc.checkins += bn.checkins || 0;
			acc.faturamento += bn.faturamento || 0;
			acc.qtd_pessoas_24 += bn.qtd_pessoas_24 || 0;
			acc.qtd_produtos_24 += bn.qtd_produtos_24 || 0;
			acc.qtd_transacoes_24 += bn.qtd_transacoes_24 || 0;
			acc.faturamento_24 += bn.faturamento_24 || 0;
			acc.checkins_24 += bn.checkins_24 || 0;
			acc.qtd_litros_cervejas += bn.qtd_litros_cervejas || 0;
			acc.faturamento_bebidas += bn.faturamento_bebidas || 0;
			acc.faturamento_comidas += bn.faturamento_comidas || 0;
			return acc;
		},
		{
			dias_operacao: 0,
			tempo_operacao_total_seg: 0,
			qtd_pessoas: 0,
			qtd_produtos: 0,
			qtd_transacoes: 0,
			checkins: 0,
			faturamento: 0,
			qtd_pessoas_24: 0,
			qtd_produtos_24: 0,
			qtd_transacoes_24: 0,
			faturamento_24: 0,
			checkins_24: 0,
			qtd_litros_cervejas: 0,
			faturamento_bebidas: 0,
			faturamento_comidas: 0,
		},
	);

	return {
		org_id: "aggregated",
		org_name: "aggregated",
		place_id: "aggregated",
		place_name: "aggregated",
		tipo_operacao: type,
		...totals,
		tempo_operacao_medio_dia_seg:
			totals.dias_operacao > 0
				? totals.tempo_operacao_total_seg / totals.dias_operacao
				: 0,
		tkt_medio_pessoas:
			totals.qtd_pessoas > 0 ? totals.faturamento / totals.qtd_pessoas : 0,
		tkt_medio_pessoas_24:
			totals.qtd_pessoas_24 > 0 ? totals.faturamento_24 / totals.qtd_pessoas_24 : 0,
		tkt_medio_checkins: totals.checkins > 0 ? totals.faturamento / totals.checkins : 0,
		tkt_medio_checkins_24:
			totals.checkins_24 > 0 ? totals.faturamento_24 / totals.checkins_24 : 0,
		tkt_medio_produtos:
			totals.qtd_produtos > 0 ? totals.faturamento / totals.qtd_produtos : 0,
		tkt_medio_produtos_24:
			totals.qtd_produtos_24 > 0 ? totals.faturamento_24 / totals.qtd_produtos_24 : 0,
		horario_pico: filtered[0]?.horario_pico || 0,
		qtd_faturamento_horario_pico: filtered[0]?.qtd_faturamento_horario_pico || 0,
		dia_semana_pico: filtered[0]?.dia_semana_pico || "",
		qtd_faturamento_dia_semana: filtered[0]?.qtd_faturamento_dia_semana || 0,
		melhor_produto: filtered[0]?.melhor_produto || "",
		qtd_faturamento_melhor_produto: filtered[0]?.qtd_faturamento_melhor_produto || 0,
		intervalo_horario: filtered[0]?.intervalo_horario || "",
		qtd_litros_cervejas: totals.qtd_litros_cervejas,
		faturamento_bebidas: totals.faturamento_bebidas,
		faturamento_comidas: totals.faturamento_comidas,
	};
}

export function aggregateProducts(
	apiResponse: GetRetrospectiveApiResponse,
): RetrospectiveData["ranking"] {
	// Usa organizationProducts que vem agregado e ordenado da API
	const organizationProducts = apiResponse.organizationProducts || [];
	// Centraliza o acesso ao primeiro elemento do array (API retorna array com 1 valor)
	const organizationInvoicing = Array.isArray(apiResponse.organizationInvoicing)
		? apiResponse.organizationInvoicing[0]
		: apiResponse.organizationInvoicing;

	// Calcular totais de faturamento de bebidas e comidas da organização
	const totalFaturamentoBebidas = organizationInvoicing?.faturamento_bebidas || 0;
	const totalFaturamentoComidas = organizationInvoicing?.faturamento_comidas || 0;

	const drinks = organizationProducts
		.filter(p => p.categoria_produto === "bebida")
		.slice(0, 5)
		.map(p => ({
			position: p.posicao_no_place,
			name: p.product_name,
			revenue: p.faturamento,
			quantity: p.qtd_produtos,
			unit: "unidades",
			percentage: safePercentage(p.faturamento, totalFaturamentoBebidas),
		}));

	const food = organizationProducts
		.filter(p => p.categoria_produto === "comida")
		.slice(0, 5)
		.map(p => {
			return {
				position: p.posicao_no_place,
				name: p.product_name,
				revenue: p.faturamento,
				quantity: p.qtd_produtos,
				unit: "unidades",
				percentage: safePercentage(p.faturamento, totalFaturamentoComidas),
			};
		});

	return { drinks, food };
}
